import { SignupFormValues } from '@porterin/booking-flow/src/auth/domain/validations/SignupValidationSchema';
import { useAuthContentContext } from '@porterin/booking-flow/src/auth/view/contexts/auth-content/AuthContentContext';
import {
  makeSignupEventLabel,
  publishAuthCtaTapEvents,
} from '@porterin/booking-flow/src/auth/view/helpers/AuthAnalyticsHelper';
import { GaEvents } from '@porterin/booking-flow/src/auth/view/stores/AuthAnalyticsStore';
import { mapPageToCategory } from '@porterin/booking-flow/src/utils/BookingFlowAnalyticsHelper';
import { Button, Checkbox, TextInput } from '@porterin/component-library/src';
import { publishEvent } from '@porterin/component-library/src/ts-utils/analytics/react-ga/EventTracker';
import Image from 'next/image';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';

import Declaration from '../common/Declaration';

interface SignupFormFieldsProps {
  form: UseFormReturn<SignupFormValues>;
  onSubmit: (data: SignupFormValues) => void;
}

const SignupFormFields: React.FC<SignupFormFieldsProps> = ({
  form,
  onSubmit,
}) => {
  const { signupModal } = useAuthContentContext();
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = form;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col space-y-6 w-full lg:justify-between lg:h-full"
    >
      <div className="flex flex-col space-y-6 sm:justify-between">
        <TextInput
          className="text-text-lg font-normal"
          icons={{
            leftIcon: (
              <Image
                src={signupModal.nameField.icon.path}
                height={16}
                width={16}
                alt={signupModal.nameField.icon.altText}
              />
            ),
          }}
          identifiers={{ id: 'name' }}
          inputOptions={{
            placeholder: signupModal.nameField.placeholder,
            error: errors.name?.message,
          }}
          autoFocus={true}
          {...register('name')}
        />
        <TextInput
          className="text-text-lg font-normal"
          icons={{
            leftIcon: (
              <Image
                src={signupModal.phoneField.icon.path}
                height={16}
                width={16}
                alt={signupModal.phoneField.icon.altText}
              />
            ),
          }}
          identifiers={{ id: 'phone' }}
          inputOptions={{
            placeholder: signupModal.phoneField.placeholder,
            error: errors.phone?.message,
          }}
          {...register('phone')}
        />
        <TextInput
          className="text-text-lg font-normal"
          icons={{
            leftIcon: (
              <Image
                src={signupModal.emailField.icon.path}
                height={16}
                width={16}
                alt={signupModal.emailField.icon.altText}
              />
            ),
          }}
          identifiers={{ id: 'email' }}
          inputOptions={{
            placeholder: signupModal.emailField.placeholder,
            error: errors.email?.message,
          }}
          {...register('email')}
        />
        <Checkbox
          identifiers={{ id: 'whatsappConsent' }}
          checkboxOptions={{
            label: signupModal.checkboxText,
            value: 'true',
          }}
          {...register('whatsappConsent', {
            onChange: event => {
              publishEvent({
                category: mapPageToCategory(window.location.pathname),
                action: event.target.checked
                  ? GaEvents.consentTap.yesConsent
                  : GaEvents.consentTap.noConsent,
              });
            },
          })}
        />
      </div>
      <div className="flex flex-col sm:flex-col-reverse sm:align-end relative max-lg:mt-[2rem]">
        {errors.root?.message && (
          <div className="text-[#eb5757] text-lg font-semibold whitespace-nowrap absolute sm:bottom-0 top-0 sm:translate-y-full -translate-y-full">
            {errors.root?.message}
          </div>
        )}
        <Button
          className="bg-[#0A57FF] text-typography-primary-inverse font-copy-lg font-semibold sm:mt-3"
          identifiers={{ id: 'signup' }}
          interactions={{
            onClick: () => {
              publishAuthCtaTapEvents(
                mapPageToCategory(window.location.pathname),
                GaEvents.formSubmit.signup,
                'signup',
                makeSignupEventLabel(
                  getValues('name'),
                  getValues('email'),
                  getValues('whatsappConsent'),
                ),
              );
            },
          }}
        >
          {signupModal.submitCta}
        </Button>
        <Declaration agreement_policy={signupModal.agreementPolicy} />
      </div>
    </form>
  );
};

export default SignupFormFields;
